<script setup>
import cn from "@/helpers/cn.js";
import InputText from "primevue/inputtext";
import { computed, ref } from "vue";

const props = defineProps({
    modelValue: {
        required: true,
    },
    round: {
        type: String,
        default: "md",
    },
    // Canonical validity FLAG (wired to PrimeVue :invalid). Message text, where
    // shown, lives on a separate `error`/`errorMessage` prop in the labeled wrappers.
    invalid: {
        type: Boolean,
        default: false,
        validator: (v) => typeof v === "boolean",
    },
    // No default: an empty/absent placeholder lets FloatLabel "on" (BaseInputLabeled)
    // keep the label resting in the field until focus/value. Standalone callers
    // pass their own placeholder.
    placeholder: {
        type: String,
        default: undefined,
    },
    // "md" = padding-only default; "lg" matches button height; "sm" is a compact variant.
    size: {
        type: String,
        default: "md",
        validator: (value) => ["sm", "md", "lg"].includes(value),
    },
});

// Border-radius is the central formField token by default; only emit a rounded-*
// class when a caller overrides `round` away from the default ("" disables it).
const rounded = computed(() => {
    switch (props.round) {
        case "xs": return "rounded-xs";
        case "sm": return "rounded-sm";
        case "md": return "rounded-md";
        case "lg": return "rounded-lg";
        case "xl": return "rounded-xl";
        case "": return "rounded-none";
        default: return "";
    }
});

const sizeClass = computed(() => {
    switch (props.size) {
        case "sm": return "h-16 md:h-18";
        case "lg": return "h-20 md:h-22";
        default: return "";
    }
});

defineEmits(["update:modelValue", "focus"]);

const input = ref(null);
defineExpose({
    focus() {
        input.value?.$el?.focus();
    },
});
</script>

<template>
    <InputText
        ref="input"
        :model-value="modelValue"
        :placeholder="placeholder"
        :invalid="invalid"
        :fluid="true"
        :class="cn('block w-full', rounded, sizeClass)"
        @update:model-value="(val) => $emit('update:modelValue', val)"
        @focus="$emit('focus')" />
</template>
